/**
 * Billing > Customers > one customer (Phase 21 / Task 21.7a): its billing account (the switch,
 * bill-to, recipients, terms) and its billing plan history (subscriptions to rate cards).
 *
 *   GET/PUT /v1/platform/billing/accounts/:tenantId
 *   GET/POST /v1/platform/billing/accounts/:tenantId/subscriptions
 *   POST /v1/platform/billing/subscriptions/:id/end
 *
 * Bill-to fields left empty fall back to the tenant's own details at invoice time (shown as
 * placeholders); they are never copied in. Changing the plan is a new subscription: the API
 * closes the open one the day before it starts.
 *
 * Every plan action says what it will do BEFORE the click (2026-10-21, after a plan was ended
 * 7 s after it was set): the current plan is stated first (red when there is none), Change and
 * End each show a sentence naming the plan and the dates, a change to the plan they're already
 * on is blocked, and an end can be undone for a few minutes.
 */
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  useBillingAccount,
  useCreateSubscription,
  useEndSubscription,
  useRateCards,
  useSaveAccount,
  useSubscriptions,
  useBillingToday,
  useUndoEndSubscription,
  type AccountResponse,
  type AccountUpdate,
  type BillingStatus,
  type Subscription,
} from './billing-hooks';
import {
  BILLING_STATUS_LABELS,
  UNDO_END_MINUTES,
  billingErrorText,
  canUndoEnd,
  changePlanPreview,
  endPlanPreview,
  nextPlan,
  planInForce,
  firstBadEmail,
  parseEmailList,
} from './billing-format';

interface AccountDraft {
  billingStatus: BillingStatus;
  billToName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  toEmails: string;
  ccEmails: string;
  termsDaysOverride: string;
  poReference: string;
  notes: string;
  showRemitBankDetails: boolean;
}

function toAccountDraft(r: AccountResponse): AccountDraft {
  const a = r.account;
  return {
    billingStatus: r.billingStatus,
    billToName: a?.billToName ?? '',
    addressLine1: a?.addressLine1 ?? '',
    addressLine2: a?.addressLine2 ?? '',
    city: a?.city ?? '',
    state: a?.state ?? '',
    postalCode: a?.postalCode ?? '',
    toEmails: (a?.toEmails ?? []).join(', '),
    ccEmails: (a?.ccEmails ?? []).join(', '),
    termsDaysOverride:
      a?.termsDaysOverride === null || a === null ? '' : String(a.termsDaysOverride),
    poReference: a?.poReference ?? '',
    notes: a?.notes ?? '',
    showRemitBankDetails: a?.showRemitBankDetails ?? false,
  };
}

const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** The draft's problems; empty when it can be saved. */
export function accountErrors(d: AccountDraft): Partial<Record<keyof AccountDraft, string>> {
  const e: Partial<Record<keyof AccountDraft, string>> = {};
  const to = parseEmailList(d.toEmails);
  const badTo = firstBadEmail(to);
  if (badTo !== null) e.toEmails = `${badTo} is not an email address`;
  else if (to.length > 20) e.toEmails = 'At most 20 addresses';
  else if (d.billingStatus === 'ACTIVE' && to.length === 0) {
    e.toEmails = 'A billed customer needs at least one address to send invoices to';
  }
  const cc = parseEmailList(d.ccEmails);
  const badCc = firstBadEmail(cc);
  if (badCc !== null) e.ccEmails = `${badCc} is not an email address`;
  else if (cc.length > 20) e.ccEmails = 'At most 20 addresses';
  const t = d.termsDaysOverride.trim();
  if (t !== '' && (!/^\d{1,3}$/.test(t) || Number(t) > 365)) {
    e.termsDaysOverride = 'A whole number of days, 0 to 365, or empty for the default';
  }
  return e;
}

export function accountBody(d: AccountDraft): AccountUpdate {
  const t = d.termsDaysOverride.trim();
  return {
    billingStatus: d.billingStatus,
    billToName: orNull(d.billToName),
    addressLine1: orNull(d.addressLine1),
    addressLine2: orNull(d.addressLine2),
    city: orNull(d.city),
    state: orNull(d.state),
    postalCode: orNull(d.postalCode),
    toEmails: parseEmailList(d.toEmails),
    ccEmails: parseEmailList(d.ccEmails),
    termsDaysOverride: t === '' ? null : Number(t),
    poReference: orNull(d.poReference),
    notes: orNull(d.notes),
    showRemitBankDetails: d.showRemitBankDetails,
  };
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '20px',
        marginBottom: '16px',
      }}
    >
      <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 14px' }}>{title}</h3>
      {children}
    </section>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  error,
  hint,
  className,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  className?: string | undefined;
}): React.JSX.Element {
  return (
    <FormRow label={label} htmlFor={id} {...(hint !== undefined ? { hint } : {})}>
      <input
        id={id}
        className={className}
        value={value}
        {...(placeholder !== undefined ? { placeholder } : {})}
        onChange={(e): void => onChange(e.target.value)}
        style={fieldStyle}
        aria-invalid={error !== undefined}
      />
      {error !== undefined && (
        <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '12px' }}>
          {error}
        </p>
      )}
    </FormRow>
  );
}

function AccountForm({
  tenantId,
  data,
}: {
  tenantId: string;
  data: AccountResponse;
}): React.JSX.Element {
  const [d, setD] = React.useState<AccountDraft>(() => toAccountDraft(data));
  React.useEffect(() => setD(toAccountDraft(data)), [data]);
  const save = useSaveAccount(tenantId, {
    onSuccess: () => toast.success('Billing account saved.'),
    onError: (err) => toast.error(billingErrorText(err, 'Could not save. Nothing was changed.')),
  });
  const set =
    (k: keyof AccountDraft) =>
    (v: string): void =>
      setD({ ...d, [k]: v });
  const errors = accountErrors(d);
  const invalid = Object.keys(errors).length > 0;
  const def = data.tenantDefaults;

  return (
    <form
      onSubmit={(e): void => {
        e.preventDefault();
        if (!invalid) save.mutate(accountBody(d));
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
    >
      <FormRow label="Billing" htmlFor="ba-status" hint="Only billed customers get invoices.">
        <select
          id="ba-status"
          value={d.billingStatus}
          onChange={(e): void => setD({ ...d, billingStatus: e.target.value as BillingStatus })}
          style={fieldStyle}
        >
          {(['ACTIVE', 'PAUSED', 'NOT_BILLED'] as const).map((s) => (
            <option key={s} value={s}>
              {BILLING_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </FormRow>
      <Field
        id="ba-to"
        className="keep-case"
        label="Send invoices to"
        hint="Separate addresses with commas."
        value={d.toEmails}
        onChange={set('toEmails')}
        placeholder={def.primaryEmail ?? ''}
        error={errors.toEmails}
      />
      <Field
        id="ba-cc"
        className="keep-case"
        label="Copy to"
        value={d.ccEmails}
        onChange={set('ccEmails')}
        error={errors.ccEmails}
      />
      <Field
        id="ba-bill-to"
        label="Bill to (name)"
        hint="Empty uses the company name."
        value={d.billToName}
        onChange={set('billToName')}
        placeholder={def.billToName}
      />
      <Field
        id="ba-address1"
        label="Bill-to address line 1"
        hint={
          def.businessAddress === null
            ? undefined
            : `Empty uses the company address: ${def.businessAddress}`
        }
        value={d.addressLine1}
        onChange={set('addressLine1')}
      />
      <Field
        id="ba-address2"
        label="Bill-to address line 2"
        value={d.addressLine2}
        onChange={set('addressLine2')}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px' }}>
        <Field id="ba-city" label="City" value={d.city} onChange={set('city')} />
        <Field id="ba-state" label="State" value={d.state} onChange={set('state')} />
        <Field id="ba-postal" label="ZIP" value={d.postalCode} onChange={set('postalCode')} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <Field
          id="ba-terms"
          label="Payment terms (days)"
          hint="Empty uses the default from Billing settings."
          value={d.termsDaysOverride}
          onChange={set('termsDaysOverride')}
          error={errors.termsDaysOverride}
        />
        <Field
          id="ba-po"
          className="keep-case"
          label="PO reference"
          value={d.poReference}
          onChange={set('poReference')}
        />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
        <input
          id="ba-remit-bank"
          type="checkbox"
          checked={d.showRemitBankDetails}
          onChange={(e): void => setD({ ...d, showRemitBankDetails: e.target.checked })}
        />
        Show bank details on invoices (Paelode&apos;s bank, routing and account number from Billing
        settings, for paying by hand)
      </label>
      <FormRow label="Notes (not printed)" htmlFor="ba-notes">
        <textarea
          id="ba-notes"
          rows={3}
          value={d.notes}
          onChange={(e): void => setD({ ...d, notes: e.target.value })}
          style={{ ...fieldStyle, resize: 'vertical' }}
        />
      </FormRow>
      <div>
        <Button type="submit" disabled={invalid || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save account'}
        </Button>
      </div>
    </form>
  );
}

/** "End plan": the last day billed, then a confirm that says what ending means. */
function EndButton({
  tenantId,
  sub,
  today,
  customerName,
}: {
  tenantId: string;
  sub: Subscription;
  today: string;
  customerName: string;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [endsOn, setEndsOn] = React.useState(today < sub.startsOn ? sub.startsOn : today);
  const end = useEndSubscription(tenantId, sub.id, {
    onSuccess: () => {
      setOpen(false);
      toast.success(
        `Billing plan ended. You can undo this for ${String(UNDO_END_MINUTES)} minutes (Undo end).`,
      );
    },
    onError: (err) => toast.error(billingErrorText(err, 'Could not end it. Nothing was changed.')),
  });
  if (!open) {
    return (
      <Button type="button" variant="outline" onClick={(): void => setOpen(true)}>
        End plan
      </Button>
    );
  }
  return (
    <div
      role="group"
      aria-label="End this billing plan"
      style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}
    >
      <input
        type="date"
        aria-label="Last day billed"
        value={endsOn}
        min={sub.startsOn}
        onChange={(e): void => setEndsOn(e.target.value)}
        style={{ ...fieldStyle, width: 'auto' }}
      />
      {endsOn !== '' && (
        <p
          data-testid="end-plan-preview"
          style={{ color: 'var(--color-danger)', fontSize: '13px', margin: 0, textAlign: 'right' }}
        >
          {endPlanPreview({ sub, customerName, endsOn })}
        </p>
      )}
      <span style={{ display: 'inline-flex', gap: '6px' }}>
        <Button
          type="button"
          disabled={endsOn === '' || endsOn < sub.startsOn || end.isPending}
          onClick={(): void => end.mutate({ endsOn })}
        >
          {end.isPending ? 'Ending…' : 'Confirm end'}
        </Button>
        <Button type="button" variant="ghost" onClick={(): void => setOpen(false)}>
          Cancel
        </Button>
      </span>
    </div>
  );
}

/**
 * "Undo end" on a plan ended in the last few minutes. It hides itself when the API's deadline
 * passes (#1014 review LOW-2); the API re-checks the window either way.
 */
function UndoEndButton({
  tenantId,
  sub,
}: {
  tenantId: string;
  sub: Subscription;
}): React.JSX.Element | null {
  const [open, setOpen] = React.useState(() => canUndoEnd(sub));
  React.useEffect(() => {
    setOpen(canUndoEnd(sub));
    if (sub.undoEndUntil === null || sub.undoEndUntil === undefined) return undefined;
    const ms = new Date(sub.undoEndUntil).getTime() - Date.now();
    if (ms <= 0) return undefined;
    const t = setTimeout(() => setOpen(false), ms);
    return (): void => clearTimeout(t);
  }, [sub]);
  const undo = useUndoEndSubscription(tenantId, sub.id, {
    onSuccess: () =>
      toast.success(
        'End undone. The billing plan is open again. If an invoice run happened since the end, check Invoices.',
      ),
    onError: (err) => toast.error(billingErrorText(err, 'Could not undo it. Nothing was changed.')),
  });
  if (!open) return null;
  return (
    <Button
      type="button"
      variant="outline"
      disabled={undo.isPending}
      onClick={(): void => undo.mutate({})}
    >
      {undo.isPending ? 'Undoing…' : 'Undo end'}
    </Button>
  );
}

/** The plan in force today, said first and plainly; red when there is none. */
function CurrentPlan({
  subs,
  today,
  customerName,
}: {
  subs: readonly Subscription[];
  today: string;
  customerName: string;
}): React.JSX.Element {
  const now = planInForce(subs, today);
  const next = nextPlan(subs, today);
  return (
    <div data-testid="current-plan" style={{ marginBottom: '14px' }}>
      {now === null ? (
        <p role="status" style={{ color: 'var(--color-danger)', fontWeight: 600, margin: 0 }}>
          No billing plan. {customerName} won&apos;t be invoiced.
        </p>
      ) : (
        <p style={{ fontWeight: 600, margin: 0 }}>
          Current plan: {now.rateCardName ?? 'Unnamed rate card'}, since {now.startsOn}
          {now.endsOn === null ? '' : `, last day ${now.endsOn}`}
        </p>
      )}
      {next !== null && (
        <p style={{ ...subText, margin: '4px 0 0' }}>
          Next: {next.rateCardName ?? 'Unnamed rate card'} from {next.startsOn}
        </p>
      )}
    </div>
  );
}

function PlanHistory({
  tenantId,
  subs,
  today,
  customerName,
}: {
  tenantId: string;
  subs: readonly Subscription[];
  today: string;
  customerName: string;
}): React.JSX.Element {
  if (subs.length === 0) {
    return <p style={subText}>No billing plan yet.</p>;
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
      <thead>
        <tr style={{ textAlign: 'left', ...subText }}>
          <th style={{ padding: '6px 8px' }}>Rate card</th>
          <th style={{ padding: '6px 8px' }}>From</th>
          <th style={{ padding: '6px 8px' }}>To</th>
          <th style={{ padding: '6px 8px' }}>Add-ons</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {subs.map((s) => (
          <tr key={s.id} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
            <td style={{ padding: '8px' }}>{s.rateCardName ?? '—'}</td>
            <td style={{ padding: '8px' }}>{s.startsOn}</td>
            <td style={{ padding: '8px' }}>{s.endsOn ?? <Pill tone="positive" label="Open" />}</td>
            <td style={{ padding: '8px', ...subText }}>
              {s.addOnCodes.length === 0 ? '—' : s.addOnCodes.join(', ')}
            </td>
            <td style={{ padding: '8px', textAlign: 'right' }}>
              {s.endsOn === null && (
                <EndButton tenantId={tenantId} sub={s} today={today} customerName={customerName} />
              )}
              {s.endsOn !== null && <UndoEndButton tenantId={tenantId} sub={s} />}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ChangePlan({
  tenantId,
  tenantType,
  today,
  subs,
}: {
  tenantId: string;
  tenantType: AccountResponse['customer']['tenantType'];
  today: string;
  subs: readonly Subscription[];
}): React.JSX.Element {
  const cards = useRateCards();
  const [rateCardId, setRateCardId] = React.useState('');
  const [startsOn, setStartsOn] = React.useState(today);
  const [addOns, setAddOns] = React.useState('');
  const [confirming, setConfirming] = React.useState(false);
  const create = useCreateSubscription(tenantId, {
    onSuccess: () => {
      setRateCardId('');
      setAddOns('');
      setConfirming(false);
      toast.success('Billing plan set.');
    },
    onError: (err) =>
      toast.error(billingErrorText(err, 'Could not set the plan. Nothing was changed.')),
  });
  // Only this customer's kind of card, not archived, and with a published price to bill from.
  const options = (cards.data?.rateCards ?? []).filter(
    (c) => c.tenantType === tenantType && !c.isArchived && c.latestPublishedVersion !== null,
  );
  const codes = addOns
    .split(/[\s,]+/)
    .map((c) => c.trim().toUpperCase())
    .filter((c) => c !== '');
  const card = options.find((c) => c.id === rateCardId);
  const preview =
    card === undefined || startsOn === ''
      ? null
      : changePlanPreview({
          subs,
          rateCardId: card.id,
          rateCardName: card.name,
          startsOn,
          addOnCodes: codes,
        });
  const blocked = preview?.kind === 'BLOCK';
  // Any edit after "Set billing plan" takes the form back out of the confirm step.
  const edit =
    (set: (v: string) => void) =>
    (v: string): void => {
      set(v);
      setConfirming(false);
    };

  return (
    <form
      onSubmit={(e): void => {
        e.preventDefault();
        if (rateCardId === '' || startsOn === '' || blocked) return;
        setConfirming(true);
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}
    >
      <h4 style={{ fontSize: '13px', fontWeight: 600, margin: 0 }}>Change billing plan</h4>
      {options.length === 0 && cards.data !== undefined && (
        <p style={subText} data-testid="no-rate-cards">
          No published rate card for this kind of customer yet.
        </p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
        <FormRow label="Rate card" htmlFor="cp-card">
          <select
            id="cp-card"
            value={rateCardId}
            onChange={(e): void => edit(setRateCardId)(e.target.value)}
            style={fieldStyle}
          >
            <option value="">Choose…</option>
            {options.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FormRow>
        <FormRow label="Starts on" htmlFor="cp-starts">
          <input
            id="cp-starts"
            type="date"
            value={startsOn}
            onChange={(e): void => edit(setStartsOn)(e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
      </div>
      <FormRow label="Add-on codes" htmlFor="cp-addons" hint="Optional, separated by commas.">
        <input
          id="cp-addons"
          value={addOns}
          onChange={(e): void => edit(setAddOns)(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      {preview !== null && (
        <p
          data-testid="change-plan-preview"
          {...(blocked ? { role: 'alert' } : {})}
          style={{
            fontSize: '13px',
            margin: 0,
            color: blocked ? 'var(--color-danger)' : 'var(--color-text-primary)',
          }}
        >
          {preview.text}
        </p>
      )}
      {confirming && !blocked ? (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            type="button"
            disabled={create.isPending}
            onClick={(): void =>
              create.mutate({
                rateCardId,
                startsOn,
                ...(codes.length > 0 ? { addOnCodes: codes } : {}),
              })
            }
          >
            {create.isPending ? 'Saving…' : 'Confirm change'}
          </Button>
          <Button type="button" variant="ghost" onClick={(): void => setConfirming(false)}>
            Back
          </Button>
        </div>
      ) : (
        <div>
          <Button type="submit" disabled={rateCardId === '' || startsOn === '' || blocked}>
            Set billing plan
          </Button>
        </div>
      )}
    </form>
  );
}

/** The Billing plan section: what is in force, the history, and the change form. */
function PlanSection({
  tenantId,
  tenantType,
  customerName,
  today,
}: {
  tenantId: string;
  tenantType: AccountResponse['customer']['tenantType'];
  customerName: string;
  today: string;
}): React.JSX.Element {
  const { data, isLoading, isError } = useSubscriptions(tenantId);
  if (isLoading) return <p style={subText}>Loading…</p>;
  if (isError || data === undefined) {
    return (
      <p role="alert" style={{ color: 'var(--color-danger)' }}>
        Could not load the billing plan history.
      </p>
    );
  }
  const subs = data.subscriptions;
  return (
    <>
      <CurrentPlan subs={subs} today={today} customerName={customerName} />
      <PlanHistory tenantId={tenantId} subs={subs} today={today} customerName={customerName} />
      <ChangePlan
        key={today}
        tenantId={tenantId}
        tenantType={tenantType}
        today={today}
        subs={subs}
      />
    </>
  );
}

export function CustomerBillingPage(): React.JSX.Element {
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const account = useBillingAccount(tenantId);
  // #940 LOW-1: the plan forms wait for the billing time zone, so their date defaults are
  // Paelode's today, never a fallback zone's.
  const today = useBillingToday();

  if (account.isLoading) return <p style={subText}>Loading…</p>;
  if (account.isError || account.data === undefined) {
    return (
      <p role="alert" style={{ color: 'var(--color-danger)' }}>
        Could not load this customer&apos;s billing.
      </p>
    );
  }
  const data = account.data;

  return (
    <div style={{ maxWidth: '820px' }}>
      <Button
        type="button"
        variant="ghost"
        onClick={(): void => {
          void navigate('/billing/customers');
        }}
      >
        <ArrowLeft size={16} aria-hidden="true" /> Customers
      </Button>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '8px 0 16px' }}>
        {data.customer.name}
      </h2>
      <Section title="Billing plan">
        {today === null ? (
          <p style={subText}>Loading…</p>
        ) : (
          <PlanSection
            tenantId={tenantId}
            tenantType={data.customer.tenantType}
            customerName={data.customer.name}
            today={today}
          />
        )}
      </Section>
      <Section title="Billing account">
        <AccountForm tenantId={tenantId} data={data} />
      </Section>
    </div>
  );
}
